import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Switch from '@mui/material/Switch';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import SyncIcon from '@mui/icons-material/Sync';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import { DataGrid } from '@mui/x-data-grid';

import { useGetCampsByUserQuery, useUpdateCampMutation } from '../../redux/services';
import { usePublishGradesMutation, useSyncGradesMutation } from '../../redux/services/camp-api';
import { useAuthContext } from '../../context/auth-context';
import { useEnqueueStacks } from '../../hooks/use-enqueue-stacks';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { CreateCampForm } from './create-form';
import { UpdateCampForm } from './update-form';

const formatDate = date => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const CampTable = () => {
  const { user } = useAuthContext();
  const userId = user?._id;
  const isMobile = useIsMobile();

  const { data: camps = [], isLoading, isFetching } = useGetCampsByUserQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true,
  });

  const [updateCamp]    = useUpdateCampMutation();
  const [publishGrades, { isLoading: publishing }] = usePublishGradesMutation();
  const [syncGrades,    { isLoading: syncing }]    = useSyncGradesMutation();
  const { enqueueSuccess, enqueueError } = useEnqueueStacks();

  const [createOpen,   setCreateOpen]   = useState(false);
  const [editCamp,     setEditCamp]     = useState(null);
  const [publishCamp,  setPublishCamp]  = useState(null);
  const [syncCamp,     setSyncCamp]     = useState(null);

  const handleConfirmPublish = async () => {
    if (!publishCamp) return;
    const willPublish = !publishCamp.gradesPublished;
    const result = await publishGrades({ _id: publishCamp._id, publish: willPublish });
    setPublishCamp(null);
    if (!result.error) {
      enqueueSuccess(willPublish ? 'Calificaciones publicadas' : 'Publicación retirada');
    } else {
      enqueueError('Error al publicar calificaciones');
    }
  };

  const handleConfirmSync = async () => {
    if (!syncCamp) return;
    const result = await syncGrades({ _id: syncCamp._id });
    setSyncCamp(null);
    if (!result.error) {
      enqueueSuccess('Snapshot sincronizado correctamente');
    } else {
      enqueueError('Error al sincronizar calificaciones');
    }
  };

  const handleStatusToggle = async camp => {
    const result = await updateCamp({ _id: camp._id, name: camp.name, dateInit: camp.dateInit, dateEnd: camp.dateEnd, status: !camp.status });
    if (!result.error) {
      enqueueSuccess(`Campamento ${!camp.status ? 'activado' : 'desactivado'}`);
    } else {
      enqueueError('Error al cambiar el estado');
    }
  };

  const columns = [
    {
      field: 'logoUrl',
      headerName: 'Logo',
      width: 64,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Avatar src={params.row.logoUrl || undefined} sx={{ width: 32, height: 32 }}>
          {!params.row.logoUrl && <NaturePeopleIcon fontSize='small' />}
        </Avatar>
      ),
    },
    { field: 'name', headerName: 'Nombre', flex: 1.5, minWidth: 180 },
    {
      field: 'dateInit',
      headerName: 'Fecha Inicio',
      flex: 1,
      minWidth: 140,
      renderCell: params => formatDate(params.row.dateInit),
    },
    {
      field: 'dateEnd',
      headerName: 'Fecha Fin',
      flex: 1,
      minWidth: 140,
      renderCell: params => formatDate(params.row.dateEnd),
    },
    {
      field: 'status',
      headerName: 'Activo',
      flex: 0.6,
      minWidth: 90,
      renderCell: params => (
        <Switch
          checked={Boolean(params.row.status)}
          onChange={() => handleStatusToggle(params.row)}
          size='small'
          color='success'
        />
      ),
    },
    {
      field: 'gradesPublished',
      headerName: 'Calificaciones',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      minWidth: 190,
      renderCell: params => {
        const published = params.row.gradesPublished;
        const publishedAt = params.row.gradesPublishedAt;
        const dateLabel = publishedAt
          ? new Date(publishedAt).toLocaleString('es', { dateStyle: 'short', timeStyle: 'short' })
          : null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={published ? `Publicado el ${dateLabel} — clic para retirar` : 'Clic para publicar calificaciones'}>
              <IconButton size='small' onClick={() => setPublishCamp(params.row)}>
                {published
                  ? <PublishIcon fontSize='small' sx={{ color: '#16a34a' }} />
                  : <UnpublishedIcon fontSize='small' sx={{ color: '#9CA3AF' }} />
                }
              </IconButton>
            </Tooltip>
            <Typography variant='caption' sx={{ color: published ? '#16a34a' : '#9CA3AF', lineHeight: 1 }}>
              {published ? 'Publicado' : 'Sin publicar'}
            </Typography>
            {published && (
              <Tooltip title='Sincronizar snapshot con notas actuales'>
                <IconButton size='small' onClick={() => setSyncCamp(params.row)}>
                  <SyncIcon fontSize='small' sx={{ color: '#2563eb' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      minWidth: 60,
      renderCell: params => (
        <Tooltip title='Editar'>
          <IconButton size='small' onClick={() => setEditCamp(params.row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant='contained' startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Nuevo Campamento
        </Button>
      </Box>

      {/* Mobile: card list */}
      {isMobile && (
        <Stack spacing={1.5}>
          {(isLoading || isFetching) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {camps.map(camp => {
            const published = camp.gradesPublished;
            return (
              <Card key={camp._id} variant='outlined'>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Avatar src={camp.logoUrl || undefined} sx={{ width: 36, height: 36 }}>
                      {!camp.logoUrl && <NaturePeopleIcon fontSize='small' />}
                    </Avatar>
                    <Typography variant='subtitle2' fontWeight={700} sx={{ flex: 1 }}>{camp.name}</Typography>
                    <Chip
                      label={published ? 'Publicado' : 'Sin publicar'}
                      size='small'
                      sx={{ color: published ? '#16a34a' : '#9CA3AF', borderColor: published ? '#16a34a' : '#d1d5db' }}
                      variant='outlined'
                    />
                  </Box>
                  <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1 }}>
                    {formatDate(camp.dateInit)} – {formatDate(camp.dateEnd)}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Switch
                      checked={Boolean(camp.status)}
                      onChange={() => handleStatusToggle(camp)}
                      size='small'
                      color='success'
                    />
                    <Typography variant='caption' color='text.secondary'>{camp.status ? 'Activo' : 'Inactivo'}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <IconButton size='small' aria-label='Editar' onClick={() => setEditCamp(camp)}>
                      <EditIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                      size='small'
                      aria-label={published ? 'Retirar publicación' : 'Publicar calificaciones'}
                      onClick={() => setPublishCamp(camp)}
                    >
                      {published
                        ? <PublishIcon fontSize='small' sx={{ color: '#16a34a' }} />
                        : <UnpublishedIcon fontSize='small' sx={{ color: '#9CA3AF' }} />}
                    </IconButton>
                    {published && (
                      <IconButton size='small' aria-label='Sincronizar snapshot' onClick={() => setSyncCamp(camp)}>
                        <SyncIcon fontSize='small' sx={{ color: '#2563eb' }} />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Desktop: DataGrid */}
      {!isMobile && (
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={camps}
            columns={columns}
            getRowId={row => row._id}
            loading={isLoading || isFetching}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            disableRowSelectionOnClick
            autoHeight
            sx={{
              border: 0,
              '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' },
            }}
          />
        </Paper>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth='sm' fullWidth fullScreen={isMobile}>
        <DialogTitle>Nuevo Campamento</DialogTitle>
        <DialogContent>
          <CreateCampForm handleClose={() => setCreateOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={Boolean(editCamp)} onClose={() => setEditCamp(null)} maxWidth='sm' fullWidth fullScreen={isMobile}>
        <DialogTitle>Editar Campamento</DialogTitle>
        <DialogContent>
          {editCamp && <UpdateCampForm camp={editCamp} handleClose={() => setEditCamp(null)} />}
        </DialogContent>
      </Dialog>

      {/* Sync confirmation dialog */}
      <Dialog open={Boolean(syncCamp)} onClose={() => setSyncCamp(null)} maxWidth='xs' fullWidth fullScreen={isMobile}>
        <DialogTitle>Sincronizar calificaciones</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Se recalcularán y publicarán las notas más recientes de "${syncCamp?.name}". Los directores verán el snapshot actualizado inmediatamente.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncCamp(null)} disabled={syncing}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSync}
            variant='contained'
            disabled={syncing}
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            sx={{
              backgroundColor: '#2563eb',
              '&:hover': { backgroundColor: '#1d4ed8' },
            }}
          >
            {syncing ? 'Sincronizando…' : 'Sincronizar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish confirmation dialog */}
      <Dialog open={Boolean(publishCamp)} onClose={() => setPublishCamp(null)} maxWidth='xs' fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {publishCamp?.gradesPublished ? 'Retirar publicación' : 'Publicar calificaciones'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {publishCamp?.gradesPublished
              ? `Los directores dejarán de ver las calificaciones de "${publishCamp?.name}" hasta la próxima publicación.`
              : `Los directores de "${publishCamp?.name}" verán las calificaciones actuales. Esta acción captura un snapshot de las notas en este momento.`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishCamp(null)} disabled={publishing}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmPublish}
            variant='contained'
            disabled={publishing}
            startIcon={publishing ? <CircularProgress size={16} /> : null}
            sx={{
              backgroundColor: publishCamp?.gradesPublished ? '#dc2626' : '#16a34a',
              '&:hover': { backgroundColor: publishCamp?.gradesPublished ? '#b91c1c' : '#15803d' },
            }}
          >
            {publishing ? 'Procesando…' : (publishCamp?.gradesPublished ? 'Retirar' : 'Publicar ahora')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
